// Staff chat MVP (SR-14): the pure reducers that fold a message, a read or an unread total into the TanStack Query
// caches, plus `applyStaffChatEvent`, which routes one hub event onto a QueryClient. Used by the live hub
// (use-staff-chat-hub.ts) and by the optimistic send (use-staff-chat.ts), so both write the caches the same way.
//
// Never log here: a message body and a patient name pass through these functions.
import { replaceEqualDeep, type InfiniteData, type QueryClient } from '@tanstack/react-query';
import type {
  LocalStaffChatMessage,
  StaffChatMessage,
  StaffChatMessageEvent,
  StaffChatPage,
  StaffChatReadEvent,
  StaffChatThread,
  StaffChatThreadEvent,
  StaffChatUnreadEvent,
} from '@/staff-chat/types/staff-chat';
import { staffChatKeys } from '@/staff-chat/services/staff-chat-keys';

export type MessagesData = InfiniteData<StaffChatPage<LocalStaffChatMessage>>;
export type ThreadsData = StaffChatPage<StaffChatThread>;

/** The thread list's preview of a message: the contract's 140 characters, '' for a deleted one. */
export const previewOf = (m: StaffChatMessage): string => (m.isDeleted ? '' : m.body.slice(0, 140));

const sameMessage = (a: LocalStaffChatMessage, b: StaffChatMessage) =>
  a.id === b.id || (!!a.clientMessageId && a.clientMessageId === b.clientMessageId);

/**
 * Folds one message into a thread's pages (newest first). It replaces the copy this browser already holds (same id, or
 * the same clientMessageId for an optimistic one), else it goes on top of the newest page. No cache: nothing to fold.
 */
export function upsertMessage(data: MessagesData | undefined, message: LocalStaffChatMessage): MessagesData | undefined {
  if (!data) return data;
  let found = false;
  const pages = data.pages.map((page) => {
    if (found) return page;
    const i = page.items.findIndex((m) => sameMessage(m, message));
    if (i < 0) return page;
    found = true;
    const items = page.items.slice();
    items[i] = message;
    return { ...page, items };
  });
  if (found) return { ...data, pages };
  if (pages.length === 0) return { ...data, pages: [{ items: [message], nextCursor: null }], pageParams: [null] };
  const [first, ...rest] = pages;
  return { ...data, pages: [{ ...first, items: [message, ...first.items] }, ...rest] };
}

/** Marks a message this browser is sending as failed (it stays in the thread with a retry). */
export function markMessageFailed(data: MessagesData | undefined, clientMessageId: string): MessagesData | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((p) => ({
      ...p,
      items: p.items.map((m) => (m.clientMessageId === clientMessageId && m.sendState ? { ...m, sendState: 'failed' as const } : m)),
    })),
  };
}

/** The pages flattened oldest first (the thread draws newest at the bottom), one copy per id (the newest copy wins). */
export function messagesOldestFirst(data: MessagesData | undefined): LocalStaffChatMessage[] {
  if (!data) return [];
  const seen = new Set<string>();
  const out: LocalStaffChatMessage[] = [];
  for (const page of data.pages)
    for (const m of page.items) {
      if (seen.has(m.id)) continue;
      seen.add(m.id);
      out.push(m);
    }
  // The server pages newest first and a new message goes on top of the newest page, so the order held IS the order: no
  // sort on createdAt, which would misplace a message being sent when this browser's clock runs behind the server's.
  return out.reverse();
}

/**
 * TanStack `structuralSharing` for a thread's messages: a re-read (reconnect, focus) replaces the pages with the server's,
 * but a message this browser is still sending, or that failed ("Not sent. Retry"), is not on the server yet. Keep those,
 * unless the server copy (same clientMessageId) has arrived.
 */
export function keepUnsentMessages(oldData: unknown, newData: unknown): unknown {
  const prev = oldData as MessagesData | undefined;
  const next = newData as MessagesData | undefined;
  if (!prev?.pages || !next?.pages) return replaceEqualDeep(oldData, newData);
  const onServer = new Set(next.pages.flatMap((p) => p.items.map((m) => m.clientMessageId).filter(Boolean)));
  const unsent = prev.pages
    .flatMap((p) => p.items)
    .filter((m) => m.sendState && !(m.clientMessageId && onServer.has(m.clientMessageId)));
  let merged: MessagesData = next;
  // Oldest first, so the newest unsent message ends on top of the newest page.
  for (const m of unsent.reverse()) merged = upsertMessage(merged, m) ?? merged;
  return replaceEqualDeep(oldData, merged);
}

export interface ThreadFoldResult {
  data: ThreadsData | undefined;
  /** The list must be read again: the message's thread is not in it, or its last message was deleted. */
  missing: boolean;
}

/**
 * Folds a new or deleted message into the thread list: the last message and time, the caller's unread count (a new
 * message from someone else, in a thread not open on this screen, adds one), and a new message moves the thread to the top.
 */
export function foldMessageIntoThreads(
  data: ThreadsData | undefined,
  message: StaffChatMessage,
  me: string | undefined,
  openThreadId: string | null,
): ThreadFoldResult {
  if (!data) return { data, missing: false };
  const i = data.items.findIndex((t) => t.id === message.threadId);
  if (i < 0) return { data, missing: true };
  const thread = data.items[i];
  if (message.isDeleted) {
    if (thread.lastMessage?.id !== message.id) return { data, missing: false };
    // The deleted message was the last one: the server's thread now names the previous live message, or none (contract).
    // Hide the deleted text at once, and read the list again for the real last message.
    const items = data.items.slice();
    items[i] = { ...thread, lastMessage: { ...thread.lastMessage, preview: '' } };
    return { data: { ...data, items }, missing: true };
  }
  // The same message again (an echo of this browser's own send, or a reconnect replay): nothing new to count.
  const seen = thread.lastMessage?.id === message.id;
  const incoming = !seen && message.senderUserId !== me && message.threadId !== openThreadId;
  const updated: StaffChatThread = {
    ...thread,
    lastMessage: { id: message.id, senderUserId: message.senderUserId, preview: previewOf(message), createdAt: message.createdAt },
    updatedAt: message.createdAt > thread.updatedAt ? message.createdAt : thread.updatedAt,
    unreadCount: incoming ? thread.unreadCount + 1 : thread.unreadCount,
  };
  const rest = data.items.filter((_, j) => j !== i);
  return { data: { ...data, items: [updated, ...rest] }, missing: false };
}

/** The caller read a thread (here or in another tab): its unread count goes to 0. Another person's read changes nothing here. */
export function foldReadIntoThreads(data: ThreadsData | undefined, threadId: string, readerUserId: string, me: string | undefined): ThreadsData | undefined {
  if (!data || readerUserId !== me) return data;
  if (!data.items.some((t) => t.id === threadId && t.unreadCount !== 0)) return data;
  return { ...data, items: data.items.map((t) => (t.id === threadId ? { ...t, unreadCount: 0 } : t)) };
}

export interface StaffChatEventContext {
  /** `${organizationId}|${userId}`: the cache owner. */
  who: string;
  /** The signed-in user's id (the token's `sub`). */
  me: string | undefined;
  /** The thread open on screen, if any: a message there is read at once, not counted. */
  openThreadId: string | null;
}

export type StaffChatEvent =
  | { name: 'StaffChatMessage'; payload: StaffChatMessageEvent }
  | { name: 'StaffChatRead'; payload: StaffChatReadEvent }
  | { name: 'StaffChatUnread'; payload: StaffChatUnreadEvent }
  | { name: 'StaffChatThread'; payload: StaffChatThreadEvent };

/** Applies one hub event to the caches. A payload missing its required fields is dropped (no throw, no log). */
export function applyStaffChatEvent(qc: QueryClient, event: StaffChatEvent, ctx: StaffChatEventContext): void {
  const keys = staffChatKeys(ctx.who);
  switch (event.name) {
    case 'StaffChatMessage': {
      const m = event.payload?.message;
      if (!m || typeof m.id !== 'string' || typeof m.threadId !== 'string') return;
      qc.setQueryData<MessagesData>(keys.messages(m.threadId), (d) => upsertMessage(d, m));
      let missing = false;
      qc.setQueryData<ThreadsData>(keys.threads(), (d) => {
        const r = foldMessageIntoThreads(d, m, ctx.me, ctx.openThreadId);
        missing = r.missing;
        return r.data;
      });
      if (missing) void qc.invalidateQueries({ queryKey: keys.threads() });
      return;
    }
    case 'StaffChatRead': {
      const p = event.payload;
      if (!p || typeof p.threadId !== 'string') return;
      qc.setQueryData<ThreadsData>(keys.threads(), (d) => foldReadIntoThreads(d, p.threadId, p.userId, ctx.me));
      qc.setQueryData<StaffChatThread>(keys.thread(p.threadId), (t) => (t && p.userId === ctx.me ? { ...t, unreadCount: 0 } : t));
      return;
    }
    case 'StaffChatUnread': {
      const total = event.payload?.total;
      if (typeof total !== 'number' || !Number.isFinite(total)) return;
      qc.setQueryData<number>(keys.unread(), Math.max(0, total));
      return;
    }
    case 'StaffChatThread': {
      const id = event.payload?.threadId;
      if (typeof id !== 'string') return;
      void qc.invalidateQueries({ queryKey: keys.threads() });
      void qc.invalidateQueries({ queryKey: keys.thread(id) });
      return;
    }
  }
}
